import { Image } from '@/components/common/image'
import { URL_DECOR } from '@/constants/ui'
import { Avatar, AvatarProps, Box, BoxProps, Typography, TypographyProps } from '@mui/material'
import React, { ReactNode } from 'react'

interface TitleIconProps {
  // Content props
  title?: string
  underline?: boolean
  icon?: ReactNode | string

  // Custom props for each component
  boxProps?: Omit<BoxProps, 'children'>
  avatarProps?: AvatarProps
  typographyProps?: TypographyProps

  // Container customization
  alignItems?: string
  justifyContent?: string
}

export const TitleIcon: React.FC<TitleIconProps> = ({
  title,
  icon,
  underline = true,
  boxProps,
  avatarProps = {
    sx: {
      backgroundColor: 'primary.main',
    },
  },
  typographyProps,
}) => {
  // Don't render if both icon and title are hidden or not provided
  if (!icon && !title) {
    return null
  }

  // Render icon element
  const renderIcon = () => {
    if (!icon) return null

    if (typeof icon === 'string') {
      return <Avatar src={icon} {...avatarProps} />
    }

    return <Avatar {...avatarProps}>{icon}</Avatar>
  }

  // Render title element
  const renderTitle = () => {
    if (!title) return null

    return (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Typography
          variant="h1"
          fontSize={{ xs: '20px', sm: '26px', md: '28px', lg: '32px' }}
          fontWeight={800}
          {...typographyProps}
        >
          {title}
        </Typography>
        {underline && (
          <Image
            src={URL_DECOR + 'dc-underline.png'}
            alt="underline"
            sx={{
              position: 'absolute',
              bottom: -12,
              left: 0,
              width: {
                xs: '100px',
                sm: '120px',
                md: '160px',
                lg: '190px',
              },
              maxWidth: '100%',
              objectFit: 'cover',
              zIndex: -1,
            }}
          />
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
        ...boxProps?.sx,
      }}
      {...boxProps}
    >
      {renderIcon()}
      {renderTitle()}
    </Box>
  )
}
